/**
 * Auth Feature Constants
 * All hard-coded values, configuration options, and enums for the authentication feature
 */

// ============================================================================
// FORM FIELD NAMES
// ============================================================================

/**
 * Login form field names
 */
export const LOGIN_FORM_FIELDS = {
  EMAIL: "email",
  PASSWORD: "password",
};

/**
 * Register form field names
 */
export const REGISTER_FORM_FIELDS = {
  EMAIL: "email",
  PASSWORD: "password",
  FULL_NAME: "fullName",
  PHONE: "phone",
  BRAND_NAME: "brandName",
  CITY: "city",
  WEBSITE: "website",
  ADDRESS: "address",
};

/**
 * Forgot password form field names
 */
export const FORGOT_PASSWORD_FORM_FIELDS = {
  EMAIL: "email",
};

/**
 * Reset password form field names
 */
export const RESET_PASSWORD_FORM_FIELDS = {
  PASSWORD: "password",
  CONFIRM_PASSWORD: "confirmPassword",
  TOKEN: "token",
};

// ============================================================================
// VALIDATION RULES
// ============================================================================

/**
 * Auth validation constants
 */
export const AUTH_VALIDATION = {
  PASSWORD_MIN_LENGTH: 8,
  FULL_NAME_MIN_LENGTH: 4,
  PHONE_MIN_LENGTH: 10,
  PHONE_MAX_LENGTH: 10,
  EMAIL_PATTERN: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
};

// ============================================================================
// ROUTE PATHS
// ============================================================================

/**
 * Auth-related route paths
 */
export const AUTH_ROUTES = {
  LOGIN: "/login",
  REGISTER: "/register",
  FORGOT_PASSWORD: "/forgot-password",
  RESET_PASSWORD: "/reset-password/:token",
  LOGOUT: "/logout",
};

// ============================================================================
// PLACEHOLDERS
// ============================================================================

/**
 * Placeholder text for auth form fields (translation keys)
 */
export const AUTH_PLACEHOLDERS = {
  EMAIL: "auth.enter",
  PASSWORD: "auth.enter",
  FULL_NAME: "auth.enter",
  PHONE: "auth.enter",
  BRAND_NAME: "auth.enter",
  CITY: "auth.enter",
  WEBSITE: "users.websitePlaceholder",
  ADDRESS: "auth.enter",
};

// ============================================================================
// TOAST MESSAGE KEYS
// ============================================================================

/**
 * Toast message translation keys for auth operations
 */
export const AUTH_TOAST_KEYS = {
  LOGGING_IN: "auth.loggingIn",
  LOGIN_SUCCESS: "auth.loginSuccess",
  REGISTER_SUCCESS: "auth.registerSuccess",
  LOGOUT_SUCCESS: "auth.logoutSuccess",
  SESSION_EXPIRED: "auth.sessionExpired",
  LOGIN_REQUIRED: "auth.loginRequired",
  INSUFFICIENT_PERMISSIONS: "auth.insufficientPermissions",
  LOADING: "messages.loading",
  SENDING_RESET_LINK: "auth.sendingResetLink",
  RESET_LINK_SENT: "auth.resetLinkSent",
  RESET_LINK_ERROR: "auth.resetLinkError",
  RESETTING_PASSWORD: "auth.resettingPassword",
  RESET_PASSWORD_SUCCESS: "auth.resetPasswordSuccess",
  RESET_PASSWORD_ERROR: "auth.resetPasswordError",
  INVALID_RESET_LINK: "auth.invalidResetLink",
  VERIFYING_RESET_LINK: "auth.verifyingResetLink",
};

// ============================================================================
// VALIDATION MESSAGE KEYS
// ============================================================================

/**
 * Validation message translation keys
 */
export const AUTH_VALIDATION_KEYS = {
  INVALID_EMAIL: "validation.invalidEmail",
  PASSWORD_MIN_LENGTH: "validation.passwordMinLength",
  FULL_NAME_MIN_LENGTH: "validation.fullNameMinLength",
  PHONE_MIN_LENGTH: "validation.phoneMinLength",
  PHONE_MAX_LENGTH: "validation.phoneMaxLength",
  REQUIRED_FIELD: "validation.required",
  PASSWORDS_MUST_MATCH: "validation.passwordsMustMatch",
};

// ============================================================================
// LOCAL STORAGE KEYS
// ============================================================================

/**
 * Local storage keys for auth data
 */
export const AUTH_STORAGE_KEYS = {
  TOKEN: "token",
  USER: "user",
  REFRESH_TOKEN: "refreshToken",
  REMEMBER_ME: "rememberMe",
};

// ============================================================================
// AUTH HEADERS
// ============================================================================

/**
 * HTTP header names for authentication
 */
export const AUTH_HEADERS = {
  AUTHORIZATION: "Authorization",
  BEARER_PREFIX: "Bearer",
};

// ============================================================================
// SESSION CONFIGURATION
// ============================================================================

/**
 * Session and token configuration
 */
export const AUTH_SESSION_CONFIG = {
  TOKEN_EXPIRY: 24 * 60 * 60 * 1000, // 24 hours in milliseconds
  REFRESH_THRESHOLD: 5 * 60 * 1000, // Refresh 5 minutes before expiry
  INACTIVITY_TIMEOUT: 30 * 60 * 1000, // 30 minutes
};

// ============================================================================
// QUERY KEYS
// ============================================================================

/**
 * React Query keys for auth-related queries
 */
// Query Keys
export const AUTH_QUERY_KEYS = {
  USER: 'user',
  PROFILE: 'profile',
  SESSION: 'session',
};
